import React, { useState, useEffect } from 'react';
import { pollsAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { BarChart3, Plus, Trash2, Eye } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminPolls() {
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['', '']);

  useEffect(() => { loadPolls(); }, []);

  const loadPolls = async () => {
    setLoading(true);
    try {
      const res = await pollsAPI.list({});
      setPolls(res.data.polls || []);
    } catch { toast.error('Failed to load polls'); }
    finally { setLoading(false); }
  };

  const createPoll = async () => {
    const validOptions = options.filter(o => o.trim());
    if (!question || validOptions.length < 2) { toast.error('Question and at least 2 options required'); return; }
    try {
      await pollsAPI.create({ question, options: validOptions });
      toast.success('Poll created');
      setShowCreate(false); setQuestion(''); setOptions(['', '']);
      loadPolls();
    } catch { toast.error('Failed'); }
  };

  const togglePoll = async (poll) => {
    try {
      await pollsAPI.update(poll.id, { is_active: !poll.is_active });
      toast.success(poll.is_active ? 'Poll closed' : 'Poll reopened');
      loadPolls();
    } catch { toast.error('Failed'); }
  };

  const deletePoll = async (id) => {
    try { await pollsAPI.delete(id); toast.success('Deleted'); loadPolls(); }
    catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-6" data-testid="admin-polls-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><BarChart3 className="w-6 h-6 text-[#B89B5E]" /> Polls</h1>
          <p className="text-sm text-[#5B616E] mt-1">Engage your audience with interactive polls</p>
        </div>
        <Button onClick={() => setShowCreate(true)} data-testid="create-poll-btn" className="bg-[#121316] text-white hover:bg-[#1B1D22]"><Plus className="w-4 h-4 mr-2" /> New Poll</Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{[1,2,3].map(i => <Skeleton key={i} className="h-48 rounded-xl" />)}</div>
      ) : polls.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {polls.map(poll => (
            <Card key={poll.id} className="bg-white border-[#E7E0D6]" data-testid="poll-card">
              <CardContent className="pt-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <Badge variant={poll.is_active ? 'default' : 'outline'} className={poll.is_active ? 'bg-[#1F7A5A] text-white text-[10px]' : 'text-[10px]'}>{poll.is_active ? 'Active' : 'Closed'}</Badge>
                    <h3 className="font-medium mt-2">{poll.question}</h3>
                    <p className="text-xs text-[#5B616E] mt-0.5">{poll.total_votes || 0} total votes</p>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => togglePoll(poll)}><Eye className="w-3 h-3" /></Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#B3261E]" onClick={() => deletePoll(poll.id)}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </div>
                <div className="space-y-2">
                  {(poll.options || []).map((opt, i) => {
                    const pct = poll.total_votes > 0 ? Math.round((opt.votes / poll.total_votes) * 100) : 0;
                    return (
                      <div key={i}>
                        <div className="flex justify-between text-xs mb-0.5">
                          <span>{opt.text}</span>
                          <span className="font-medium">{pct}% ({opt.votes})</span>
                        </div>
                        <Progress value={pct} className="h-2" />
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 text-[#5B616E]">
          <BarChart3 className="w-16 h-16 mx-auto mb-4 opacity-30" />
          <p>No polls yet</p>
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Create Poll</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><label className="text-sm font-medium mb-1 block">Question</label><Input value={question} onChange={e => setQuestion(e.target.value)} data-testid="poll-question-input" /></div>
            <div>
              <label className="text-sm font-medium mb-1 block">Options</label>
              <div className="space-y-2">
                {options.map((opt, i) => (
                  <div key={i} className="flex gap-2">
                    <Input value={opt} onChange={e => { const n = [...options]; n[i] = e.target.value; setOptions(n); }} placeholder={`Option ${i + 1}`} data-testid={`poll-option-${i}`} />
                    {options.length > 2 && <Button variant="ghost" size="sm" className="text-[#B3261E]" onClick={() => setOptions(options.filter((_, j) => j !== i))}>Remove</Button>}
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => setOptions([...options, ''])}>+ Add Option</Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={createPoll} className="bg-[#121316] text-white hover:bg-[#1B1D22]">Create Poll</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
